import { useEffect, useState } from 'react';
import { setValmislauseTagSections, getTemplateByType } from '../api/api';

const SENTINEL_RE = /\{\{section_template_([^}]+)\}\}/;

// A report type's chapter list comes from its real template report
// (reports.template = true, same row "Raporttipohjat" edits) rather than a
// hand-maintained client list — so a new report type, or a template's
// sections changing, shows up here automatically with no code change.
//
// Dynamic-category report types (julkisivu, ikkuna-ovi — see
// kuntoarvioSectionSync.js/julkisivuService.js on the backend) don't have
// one fixed `type` per chapter the way the others do: their real sections
// are cloned lazily per inspection target, all sharing one `type` per
// category (`section_chip_<category>`). Their template report instead
// holds a "sentinel" row per category, named `{{section_template_<Category>}}`
// with `type: 'unknown'` — decoded here into that same `section_chip_*` key,
// so scoping lines up with the real cloned sections either way.
// Boilerplate sections every report template carries but that are never a
// meaningful valmislause scope — same convention backend's
// kuntoarvioSectionSync.js already uses to recognize them (type OR name,
// lowercased, since which one actually holds 'kansisivu'/'allekirjoitukset'
// varies by template).
const NON_CHAPTER_KEYS = new Set(['kansisivu', 'allekirjoitukset']);

function chaptersFromTemplate(template) {
  const seen = new Set();
  const chapters = [];
  for (const s of template?.report_sections ?? []) {
    const m = s.name?.match(SENTINEL_RE);
    const key = m ? `section_chip_${m[1].toLowerCase()}` : s.type;
    const label = m ? m[1] : (s.name?.trim() || s.type);
    if (!key || seen.has(key)) continue;
    if (NON_CHAPTER_KEYS.has(key?.toLowerCase()) || NON_CHAPTER_KEYS.has(label?.toLowerCase())) continue;
    seen.add(key);
    chapters.push({ key, label });
  }
  return chapters;
}

// reportType -> chapters[] (resolved) | Promise<chapters[]> (in flight) —
// shared across every picker/rail on the page, so switching between tags
// scoped to the same report type doesn't refetch its template every time.
const chapterCache = new Map();

export function useReportChaptersMap(reportTypes) {
  const key = reportTypes.join('\u0000');
  const [map, setMap] = useState(() => {
    const m = {};
    for (const rt of reportTypes) {
      const cached = chapterCache.get(rt);
      if (Array.isArray(cached)) m[rt] = cached;
    }
    return m;
  });

  useEffect(() => {
    let cancelled = false;
    reportTypes.forEach(rt => {
      if (!rt) return;
      const cached = chapterCache.get(rt);
      if (Array.isArray(cached)) {
        setMap(prev => (prev[rt] ? prev : { ...prev, [rt]: cached }));
        return;
      }
      // `cached` here is either undefined (nobody has asked for this report
      // type yet) or a Promise another caller already started — ValmislauseBrowser's
      // own rail and the selected tag's ChapterScopePicker both call this hook
      // for the same report type in the same render, so whichever's effect
      // runs second used to just bail out here ("a fetch is already in
      // flight") without ever subscribing to the result, leaving ITS OWN
      // chapterDefs empty forever (the rail silently falling back to the
      // flat tag list) even after the fetch the other caller started had
      // long since resolved. Attaching to the same shared promise instead —
      // a promise can have any number of .then() subscribers — means every
      // caller watching this report type gets notified once, however many
      // of them raced to ask for it first.
      const promise = cached ?? getTemplateByType(rt).then(r => chaptersFromTemplate(r.data)).catch(() => []);
      if (!cached) chapterCache.set(rt, promise);
      promise.then(chs => {
        chapterCache.set(rt, chs);
        if (!cancelled) setMap(prev => ({ ...prev, [rt]: chs }));
      });
    });
    return () => { cancelled = true; };
    // reportTypes is a fresh array each render — `key` is its stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return map;
}

// Chapter ("kappale") scope picker for one valmislause tag — same single-
// dropdown pattern as TagReportTypesPicker (trigger shows the current
// selection directly, no separate chip row), one level more specific: a
// chapter is only meaningful paired with one of the tag's already-chosen
// report types (a report type's section keys aren't shared with other
// report types), so this renders one dropdown row per report type the tag
// is already scoped to, and does nothing for a fully-universal tag (no
// report type to pick chapters from in the first place).
//
// `tag` needs { id, report_types, sections } where sections is
// [{report_type, section_type}]. `onChange(newSections)` is called with the
// full flat list after a successful save, same "caller patches its own
// state" contract as TagReportTypesPicker.
export default function ChapterScopePicker({ tag, onChange }) {
  const [busy, setBusy] = useState(false);
  const [openFor, setOpenFor] = useState(null); // the report type whose panel is open, or null
  const current = tag.sections || [];
  const reportTypes = tag.report_types || [];
  const chaptersByType = useReportChaptersMap(reportTypes);

  if (reportTypes.length === 0) return null;

  const save = async (nextSections) => {
    setBusy(true);
    try {
      await setValmislauseTagSections(tag.id, nextSections);
      onChange?.(nextSections);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (reportType, key) => {
    const has = current.some(s => s.report_type === reportType && s.section_type === key);
    save(has
      ? current.filter(x => !(x.report_type === reportType && x.section_type === key))
      : [...current, { report_type: reportType, section_type: key }]);
  };

  return (
    <div className="d-flex flex-column gap-2" style={{ fontSize: '0.8rem' }}>
      {reportTypes.map(reportType => {
        const options = chaptersByType[reportType] || [];
        const selected = current.filter(s => s.report_type === reportType);
        const isOpen = openFor === reportType;
        const triggerLabel = selected.length === 0
          ? 'Kaikki kappaleet'
          : selected.length === 1
            ? (options.find(o => o.key === selected[0].section_type)?.label || selected[0].section_type)
            : `${selected.length} kappaletta valittu`;
        return (
          <div key={reportType} className="d-flex align-items-center gap-2" style={{ position: 'relative' }}>
            <span className="text-muted">Kappale ({reportType}):</span>
            <button type="button" onClick={() => setOpenFor(isOpen ? null : reportType)} disabled={busy}
              className="d-flex align-items-center gap-2"
              aria-expanded={isOpen}
              style={{
                fontSize: '0.78rem', padding: '4px 10px', borderRadius: 999, border: '1px solid #dee2e6',
                background: '#fff', color: selected.length ? '#198754' : '#6c757d', cursor: 'pointer',
              }}>
              {triggerLabel}
              <i className="bi bi-chevron-down" style={{ fontSize: '0.63rem', color: '#adb5bd' }} />
            </button>
            {isOpen && (
              <>
                <div onClick={() => setOpenFor(null)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
                <div style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: 4, width: 260, maxHeight: 280, overflowY: 'auto',
                  background: '#fff', border: '1px solid #e4e6e8', borderRadius: 10, boxShadow: '0 8px 24px rgba(20,20,22,0.12)',
                  padding: 6, zIndex: 21,
                }}>
                  {options.length === 0 && (
                    <div style={{ padding: '6px 8px', fontSize: '0.78rem', color: '#adb5bd', fontStyle: 'italic' }}>Ei kappaleita.</div>
                  )}
                  {options.map(o => (
                    <label key={o.key} className="d-flex align-items-center gap-2"
                      style={{ padding: '6px 8px', fontSize: '0.8rem', borderRadius: 6, cursor: busy ? 'default' : 'pointer' }}>
                      <input type="checkbox" checked={selected.some(s => s.section_type === o.key)} disabled={busy}
                        onChange={() => toggle(reportType, o.key)} />
                      {o.label}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
